'use client';

import React, { memo } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { motion, AnimatePresence } from 'framer-motion';
import { gridItemVariants } from '@/lib/animations';
import {
  HomeIcon,
  CalendarIcon,
  DocumentTextIcon,
  HeartIcon,
  UserIcon,
  EnvelopeIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
} from '@heroicons/react/24/outline';
import {
  HomeIcon as HomeIconSolid,
  CalendarIcon as CalendarIconSolid,
  DocumentTextIcon as DocumentTextIconSolid,
  HeartIcon as HeartIconSolid,
  UserIcon as UserIconSolid,
  EnvelopeIcon as EnvelopeIconSolid,
  Cog6ToothIcon as Cog6ToothIconSolid,
} from '@heroicons/react/24/solid';
import { useCustomerAuth } from '@/contexts/CustomerAuthContext';
import type { PageUrlMap } from '@/types/blog';
import { useCustomerPanelUrlBuilder, useCustomerPanelTranslations } from '@/lib/customer-panel-utils';

interface SidebarNavProps {
  isOpen: boolean;
  onClose: () => void;
  language?: string;
  translations?: Record<string, string>;
  pageUrls?: PageUrlMap;
  defaultLanguage?: string;
}

interface NavItem {
  key: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  iconSolid: React.ComponentType<{ className?: string }>;
  translationKey: string;
}

function SidebarNav({
  isOpen,
  onClose,
  language = 'en',
  translations = {},
  pageUrls,
  defaultLanguage = 'en',
}: SidebarNavProps) {
  const t = useCustomerPanelTranslations(translations);
  const buildUrl = useCustomerPanelUrlBuilder(pageUrls, language, defaultLanguage);
  const { logout } = useCustomerAuth();
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();

  // Navigation items - artık href yerine sadece key kullanıyoruz
  const navItems: NavItem[] = [
    {
      key: 'dashboard',
      href: '#', // Artık kullanılmayacak, sadece geriye dönük uyumluluk için
      icon: HomeIcon,
      iconSolid: HomeIconSolid,
      translationKey: 'customer_panel.sidebar.dashboard',
    },
    {
      key: 'reservations',
      href: '#',
      icon: CalendarIcon,
      iconSolid: CalendarIconSolid,
      translationKey: 'customer_panel.sidebar.reservations',
    },
    {
      key: 'invoices',
      href: '#',
      icon: DocumentTextIcon,
      iconSolid: DocumentTextIconSolid,
      translationKey: 'customer_panel.sidebar.invoices',
    },
    {
      key: 'favorites',
      href: '#',
      icon: HeartIcon,
      iconSolid: HeartIconSolid,
      translationKey: 'customer_panel.sidebar.favorites',
    },
    {
      key: 'profile',
      href: '#',
      icon: UserIcon,
      iconSolid: UserIconSolid,
      translationKey: 'customer_panel.sidebar.profile',
    },
    {
      key: 'newsletter',
      href: '#',
      icon: EnvelopeIcon,
      iconSolid: EnvelopeIconSolid,
      translationKey: 'customer_panel.sidebar.newsletter',
    },
    {
      key: 'settings',
      href: '#',
      icon: Cog6ToothIcon,
      iconSolid: Cog6ToothIconSolid,
      translationKey: 'customer_panel.sidebar.settings',
    },
  ];

  const handleLogout = async () => {
    try {
      await logout();
      const loginUrl = buildUrl('login');
      if (loginUrl !== '#') {
        window.location.href = loginUrl;
      }
    } catch (error) {
      // Logout error handled silently - user will be redirected anyway
    }
  };

  // Query parameter'dan aktif page'i oku
  const activePage = searchParams?.get('page') || 'dashboard';

  const isActive = (pageKey: string) => {
    return activePage === pageKey;
  };

  // Page navigation handler - query parameter'ı güncelle
  const handlePageNavigation = (pageKey: string) => {
    if (isActive(pageKey)) {
      onClose();
      return;
    }

    // Mevcut pathname'i koru, sadece query parameter'ı güncelle
    const currentPath = pathname || '/customer';
    const newUrl = `${currentPath}?page=${pageKey}`;
    
    // Router.push ile client-side navigation (sayfa yenilenmez)
    router.push(newUrl);
    onClose();
  };

  return (
    <>
      {/* Mobile sidebar */}
      <AnimatePresence>
        {isOpen && (
          <>
            <motion.aside
              initial={{ x: -300 }}
              animate={{ x: 0 }}
              exit={{ x: -300 }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed inset-y-0 left-0 z-50 w-64 bg-white drop-shadow-card lg:hidden"
              role="navigation"
              aria-label={t('customer_panel.sidebar.navigation', 'Main navigation')}
            >
              <div className="flex flex-col h-full">
                <div className="flex-1 overflow-y-auto py-4">
                  <nav className="space-y-1 px-2" role="navigation" aria-label={t('customer_panel.sidebar.navigation', 'Main navigation')}>
                    {navItems.map((item) => {
                      const active = isActive(item.key);
                      const Icon = active ? item.iconSolid : item.icon;
                      
                      return (
                        <button type="button"
                          key={item.key}
                          onClick={() => handlePageNavigation(item.key)}
                          className={`
                            group flex items-center w-full px-3 py-2 text-sm font-medium rounded-md
                            focus:outline-none focus:ring-2 focus:ring-alsero-first focus:ring-offset-2
                            ${active
                              ? 'bg-alsero-first text-white border-alsero-first cursor-default'
                              : 'text-gray-700 hover:bg-alsero-first hover:text-white hover:border-alsero-first cursor-pointer'
                            }
                          `}
                          aria-current={active ? 'page' : undefined}
                        >
                          <Icon
                            className={`
                              mr-3 h-5 w-5 flex-shrink-0
                              ${active ? 'text-white' : 'text-gray-400 group-hover:text-white'}
                            `}
                            aria-hidden="true"
                          />
                          {t(item.translationKey, item.key)}
                        </button>
                      );
                    })}
                  </nav>
                </div>
                
                <div className="border-t border-gray-200 p-4">
                  <button type="button"
                    onClick={handleLogout}
                    className="group flex items-center w-full px-3 py-2 text-sm font-medium text-gray-700 rounded-md hover:bg-gray-50 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                    aria-label={t('customer_panel.sidebar.logout', 'Logout')}
                  >
                    <ArrowRightOnRectangleIcon className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500" aria-hidden="true" />
                    {t('customer_panel.sidebar.logout', 'Logout')}
                  </button>
                </div>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Desktop sidebar - Right side, not fixed */}
      <aside 
        className="hidden lg:block"
        role="navigation"
        aria-label={t('customer_panel.sidebar.navigation', 'Main navigation')}
      >
        <motion.div 
          className=""
          variants={gridItemVariants}
          initial="hidden"
          animate="visible"
        >
          <h3 className="flex font-semibold text-alsero-dark-gray text-2xl border-l-[2px] border-alsero-first pl-3 mb-6">
            {t('customer_panel.sidebar.title', 'Menu')}
          </h3>
            <nav className="space-y-2" role="navigation" aria-label={t('customer_panel.sidebar.navigation', 'Main navigation')}>
              {navItems.map((item, index) => {
                const active = isActive(item.key);
                const Icon = active ? item.iconSolid : item.icon;
                
                return (
                  <motion.div
                    key={item.key}
                    className="group"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: index * 0.1 }}
                    whileHover={active ? {} : { scale: 1.03, x: 5 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <button type="button"
                      onClick={() => handlePageNavigation(item.key)}
                      className={active
                        ? 'flex flex-row justify-between items-center w-full px-5 py-4 bg-alsero-first text-white border-alsero-first border transition-colors ease-in-out duration-300 cursor-default focus:outline-none focus:ring-2 focus:ring-alsero-first focus:ring-offset-2'
                        : 'flex flex-row justify-between items-center w-full px-5 py-4 bg-white text-alsero-dark-gray border border-[#EDEDED] hover:bg-alsero-first hover:text-white hover:border-alsero-first transition-colors ease-in-out duration-300 cursor-pointer focus:outline-none focus:ring-2 focus:ring-alsero-first focus:ring-offset-2'
                      }
                      aria-current={active ? 'page' : undefined}
                    >
                      <div className="flex items-center gap-3">
                        <Icon
                          className={`
                            h-5 w-5 flex-shrink-0
                            ${active ? 'text-white' : 'text-gray-400 group-hover:text-white'}
                          `}
                          aria-hidden="true"
                        />
                        <span className="font-medium text-[15px]">{t(item.translationKey, item.key)}</span>
                      </div>
                    </button>
                  </motion.div>
                );
              })}
            </nav>
          
          {/* Logout button */}
          <motion.div
            className="mt-6"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: navItems.length * 0.1 }}
            whileHover={{ scale: 1.03, x: 5 }}
            whileTap={{ scale: 0.98 }}
          >
            <button type="button"
              onClick={handleLogout}
              className="group flex flex-row justify-between items-center w-full px-5 py-4 bg-white transition-colors ease-in-out duration-300 border border-red-200 hover:bg-red-600 hover:text-white hover:border-red-600 text-alsero-dark-gray focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
            >
              <div className="flex items-center gap-3">
                <ArrowRightOnRectangleIcon className="h-5 w-5 text-gray-400 group-hover:text-white" />
                <span className="font-medium text-[15px]">{t('customer_panel.sidebar.logout', 'Logout')}</span>
              </div>
            </button>
          </motion.div>
        </motion.div>
      </aside>
    </>
  );
}

// Memoize edilmiş sidebar - sadece props veya pathname değiştiğinde render edilir
// URL değiştiğinde sadece aktif item güncellenir, menü sabit kalır
export default memo(SidebarNav, (prevProps, nextProps) => {
  // Sidebar ile ilgili props değişmediyse re-render yapma
  // pathname değişikliği usePathname hook'u ile içeride handle edilir
  return (
    prevProps.isOpen === nextProps.isOpen &&
    prevProps.language === nextProps.language &&
    prevProps.defaultLanguage === nextProps.defaultLanguage &&
    JSON.stringify(prevProps.translations) === JSON.stringify(nextProps.translations) &&
    JSON.stringify(prevProps.pageUrls) === JSON.stringify(nextProps.pageUrls)
  );
});

